import { useState, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import TasksTab from './Show/TasksTab';
import QuotationTab from './Show/QuotationTab';
import SupplierTab from './Show/SupplierTab';
import PaymentTab from './Show/PaymentTab';

export default function QueriesShow({
    query,
    tasks = [],
    activityLogs = [],
    users = [],
    quotations = [],
    supplierBookings = [],
    payments = [],
    suppliers = [],
    currencies = [],
    statuses,
    statusColors,
    quotationStatuses = {},
    quotationStatusColors = {},
    types,
    travelTypes = {},
    priorities,
    priorityColors,
    sources,
}) {
    const [activeTab, setActiveTab] = useState('quotation');
    const [selectedServices, setSelectedServices] = useState([]);

    // Status configuration matching legacy
    const statusConfig = {
        pending: { label: 'Pending', color: '#f69f00', icon: 'bi-hourglass-split' },
        reverted: { label: 'Reverted', color: '#c20e97', icon: 'bi-arrow-return-left' },
        followup: { label: 'Follow-up', color: '#8f8b8b', icon: 'bi-telephone' },
        proposal_sent: { label: 'Proposal Sent', color: '#0d84f0', icon: 'bi-file-earmark-text' },
        confirmed: { label: 'Confirmed', color: '#1cb94c', icon: 'bi-check-circle' },
        cancelled: { label: 'Cancelled', color: '#cf1010', icon: 'bi-x-circle' },
        lost: { label: 'Lost', color: '#000', icon: 'bi-emoji-frown' },
        completed: { label: 'Completed', color: '#654c92', icon: 'bi-trophy' },
    };

    const currentStatus = statusConfig[query.status] || statusConfig.pending;

    // Formatters
    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatDateTime = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency', currency: 'INR', maximumFractionDigits: 0
        }).format(amount);
    };

    // Avatar color
    const getAvatarColor = (name) => {
        const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];
        return colors[name ? name.charCodeAt(0) % colors.length : 0];
    };

    const clientName = query.guest_name || query.client?.name || 'Guest';

    // Handlers
    const handleStatusChange = (newStatus) => {
        router.patch(`/queries/${query.id}/status`, { status: newStatus }, { preserveScroll: true });
    };

    // Workflow checklist items
    const workflowItems = [
        { id: 'guests', label: 'Manage Guest Info', icon: 'bi-people', done: false },
        { id: 'docs', label: 'Upload Guest Documents', icon: 'bi-folder', done: false },
        { id: 'confirm', label: 'Make Query Confirm', icon: 'bi-check-circle', done: query.status === 'confirmed' || query.status === 'completed' },
        { id: 'invoice', label: 'Create/Check Invoice', icon: 'bi-receipt', done: false },
        { id: 'voucher', label: 'Create/Check Voucher', icon: 'bi-file-earmark', done: false },
    ];

    // Mock data for communications (will come from backend later)
    const communications = [];

    // Count for supplier tab
    const pendingSupplierBookings = supplierBookings.filter(b => b.status === 'pending' || b.status === 'requested').length;

    // Get pending tasks count for badge - MUST be defined before tabs array
    const pendingTasksCount = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress').length;

    // Main tabs matching legacy
    const tabs = [
        { id: 'communication', label: 'Communication', icon: 'bi-envelope', count: communications.length },
        { id: 'tasks', label: 'Task/Follow-ups', icon: 'bi-list-task', count: pendingTasksCount },
        { id: 'quotation', label: 'Quotation', icon: 'bi-file-earmark-text', count: quotations.length },
        { id: 'supplier', label: 'Supplier', icon: 'bi-building', count: pendingSupplierBookings },
        { id: 'payment', label: 'Payment', icon: 'bi-credit-card', count: payments.length },
        { id: 'log', label: 'Query Log', icon: 'bi-clock-history', count: activityLogs.length },
    ];

    return (
        <AppLayout title={query.query_number}>
            <div className="query-detail-page">
                {/* Top Bar */}
                <div className="qd-topbar">
                    <div className="qd-topbar-left">
                        <Link href="/queries" className="qd-back-link">
                            <i className="bi bi-arrow-left"></i>
                            <span>Back to Queries</span>
                        </Link>
                        <div className="qd-query-badge">
                            <span className="qd-query-id">{query.query_number}</span>
                            <span
                                className="qd-status-badge"
                                style={{ backgroundColor: currentStatus.color }}
                            >
                                {currentStatus.label}
                            </span>
                        </div>
                    </div>
                    <div className="qd-topbar-right">
                        <div className="qd-topbar-actions">
                            {query.contact_phone && (
                                <a href={`https://wa.me/${query.contact_phone.replace(/[^0-9]/g, '')}`}
                                   target="_blank" rel="noopener"
                                   className="qd-action-btn qd-whatsapp">
                                    <i className="bi bi-whatsapp"></i>
                                </a>
                            )}
                            <Link href={`/queries/${query.id}/edit`} className="qd-action-btn">
                                <i className="bi bi-pencil"></i>
                            </Link>
                            <button className="qd-action-btn qd-primary">
                                <i className="bi bi-printer"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main 3-Column Layout */}
                <div className="qd-layout">
                    {/* LEFT SIDEBAR - Query Information */}
                    <div className="qd-sidebar qd-sidebar-left">
                        {/* Status Bar */}
                        <div className="qd-status-bar" style={{ backgroundColor: currentStatus.color }}>
                            <i className={`bi ${currentStatus.icon}`}></i>
                            <span>{currentStatus.label}</span>
                            <select
                                className="qd-status-select"
                                value={query.status}
                                onChange={(e) => handleStatusChange(e.target.value)}
                            >
                                {Object.entries(statuses).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Client Info */}
                        <div className="qd-info-section">
                            <div className="qd-client-card">
                                <div className="qd-client-avatar" style={{ backgroundColor: getAvatarColor(clientName) }}>
                                    {clientName.substring(0, 2).toUpperCase()}
                                </div>
                                <div className="qd-client-details">
                                    <div className="qd-client-name">{clientName}</div>
                                    {query.client && (
                                        <Link href={`/clients/${query.client.id}`} className="qd-view-link">
                                            View Client
                                        </Link>
                                    )}
                                </div>
                            </div>

                            {query.contact_email && (
                                <a href={`mailto:${query.contact_email}`} className="qd-contact-item">
                                    <i className="bi bi-envelope"></i>
                                    <span>{query.contact_email}</span>
                                </a>
                            )}
                            {query.contact_phone && (
                                <a href={`tel:${query.contact_phone}`} className="qd-contact-item">
                                    <i className="bi bi-phone"></i>
                                    <span>{query.contact_phone}</span>
                                </a>
                            )}
                        </div>

                        {/* Query Details */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Query Details</div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Query ID</span>
                                <span className="qd-info-value">{query.query_number}</span>
                            </div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Title</span>
                                <span className="qd-info-value">{query.title || '-'}</span>
                            </div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Travel Date</span>
                                <span className="qd-info-value">{formatDate(query.travel_date)}</span>
                            </div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Return Date</span>
                                <span className="qd-info-value">{formatDate(query.return_date)}</span>
                            </div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Duration</span>
                                <span className="qd-info-value">{query.nights || 0}N / {(query.nights || 0) + 1}D</span>
                            </div>
                        </div>

                        {/* Destinations */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Destinations</div>
                            {query.destinations && query.destinations.length > 0 ? (
                                <div className="qd-destinations-list">
                                    {query.destinations.map((dest, i) => (
                                        <div key={i} className="qd-destination-item">
                                            <i className="bi bi-geo-alt"></i>
                                            <span>{dest.name}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="qd-no-data">No destinations specified</div>
                            )}
                        </div>

                        {/* Pax Information */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Pax Information</div>
                            <div className="qd-pax-grid">
                                <div className="qd-pax-item">
                                    <span className="qd-pax-count">{query.adults}</span>
                                    <span className="qd-pax-label">Adults</span>
                                </div>
                                <div className="qd-pax-item">
                                    <span className="qd-pax-count">{query.children || 0}</span>
                                    <span className="qd-pax-label">Children</span>
                                </div>
                                <div className="qd-pax-item">
                                    <span className="qd-pax-count">{query.infants || 0}</span>
                                    <span className="qd-pax-label">Infants</span>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Additional Info</div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Query Type</span>
                                <span className="qd-info-value qd-badge">{types[query.query_type]}</span>
                            </div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Lead Source</span>
                                <span className="qd-info-value">{sources[query.source] || '-'}</span>
                            </div>

                            <div className="qd-info-row">
                                <span className="qd-info-label">Priority</span>
                                <span className={`qd-info-value qd-priority qd-priority-${query.priority}`}>
                                    {query.priority === 'hot' && <i className="bi bi-fire"></i>}
                                    {priorities[query.priority]}
                                </span>
                            </div>

                            {query.assigned_to && (
                                <div className="qd-info-row">
                                    <span className="qd-info-label">Assigned To</span>
                                    <span className="qd-info-value">{query.assigned_to.name}</span>
                                </div>
                            )}

                            <div className="qd-info-row">
                                <span className="qd-info-label">Created</span>
                                <span className="qd-info-value qd-info-small">{formatDateTime(query.created_at)}</span>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT - Tabs */}
                    <div className="qd-main">
                        {/* Tab Navigation */}
                        <div className="qd-tabs">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`qd-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <i className={`bi ${tab.icon}`}></i>
                                    <span>{tab.label}</span>
                                    {tab.count > 0 && (
                                        <span className="qd-tab-badge">{tab.count}</span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="qd-tab-content">
                            {/* Communication Tab */}
                            {activeTab === 'communication' && (
                                <div className="qd-panel">
                                    <div className="qd-panel-header">
                                        <h3>Communication</h3>
                                        <button className="btn btn-primary btn-sm">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            Send Email
                                        </button>
                                    </div>
                                    <div className="qd-panel-body">
                                        {communications.length === 0 ? (
                                            <div className="qd-empty">
                                                <i className="bi bi-envelope-open"></i>
                                                <h4>No Communications Yet</h4>
                                                <p>Send an email to start the conversation with the client.</p>
                                                <button className="btn btn-primary">
                                                    <i className="bi bi-envelope me-1"></i>
                                                    Compose Email
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="qd-email-list">
                                                {/* Email threads will go here */}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Tasks Tab */}
                            {activeTab === 'tasks' && (
                                <TasksTab
                                    query={query}
                                    tasks={tasks}
                                    users={users}
                                />
                            )}

                            {/* Quotation Tab */}
                            {activeTab === 'quotation' && (
                                <QuotationTab
                                    query={query}
                                    quotations={quotations}
                                    statuses={quotationStatuses}
                                    statusColors={quotationStatusColors}
                                />
                            )}

                            {/* Supplier Tab */}
                            {activeTab === 'supplier' && (
                                <SupplierTab
                                    query={query}
                                    supplierBookings={supplierBookings}
                                    suppliers={suppliers}
                                    currencies={currencies}
                                    quotations={quotations}
                                />
                            )}

                            {/* Payment Tab */}
                            {activeTab === 'payment' && (
                                <PaymentTab
                                    query={query}
                                    payments={payments}
                                    quotations={quotations}
                                    currencies={currencies}
                                />
                            )}

                            {/* Query Log Tab */}
                            {activeTab === 'log' && (
                                <div className="qd-panel">
                                    <div className="qd-panel-header">
                                        <h3>Query Log / History</h3>
                                    </div>
                                    <div className="qd-panel-body">
                                        <div className="qd-log-list">
                                            {activityLogs.length > 0 ? (
                                                activityLogs.map(log => (
                                                    <div key={log.id} className="qd-log-item">
                                                        <div
                                                            className="qd-log-icon"
                                                            style={{ backgroundColor: log.action_color || '#64748b' }}
                                                        >
                                                            <i className={`bi ${log.action_icon || 'bi-circle'}`}></i>
                                                        </div>
                                                        <div className="qd-log-content">
                                                            <div className="qd-log-title">
                                                                <strong>{log.user?.name || 'System'}</strong>{' '}
                                                                {log.description || log.action_label}
                                                            </div>
                                                            {log.change_summary && (
                                                                <div className="qd-log-changes">{log.change_summary}</div>
                                                            )}
                                                            <div className="qd-log-time">{formatDateTime(log.created_at)}</div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <>
                                                    <div className="qd-log-item">
                                                        <div className="qd-log-icon" style={{ backgroundColor: '#10b981' }}>
                                                            <i className="bi bi-plus-lg"></i>
                                                        </div>
                                                        <div className="qd-log-content">
                                                            <div className="qd-log-title">
                                                                <strong>{query.created_by?.name || 'System'}</strong> created this query
                                                            </div>
                                                            <div className="qd-log-time">{formatDateTime(query.created_at)}</div>
                                                        </div>
                                                    </div>

                                                    {query.updated_at !== query.created_at && (
                                                        <div className="qd-log-item">
                                                            <div className="qd-log-icon" style={{ backgroundColor: '#06b6d4' }}>
                                                                <i className="bi bi-pencil"></i>
                                                            </div>
                                                            <div className="qd-log-content">
                                                                <div className="qd-log-title">Query was updated</div>
                                                                <div className="qd-log-time">{formatDateTime(query.updated_at)}</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {query.confirmed_at && (
                                                        <div className="qd-log-item">
                                                            <div className="qd-log-icon" style={{ backgroundColor: '#1cb94c' }}>
                                                                <i className="bi bi-check-lg"></i>
                                                            </div>
                                                            <div className="qd-log-content">
                                                                <div className="qd-log-title">Query confirmed</div>
                                                                <div className="qd-log-time">{formatDateTime(query.confirmed_at)}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT SIDEBAR - Workflow & Summary */}
                    <div className="qd-sidebar qd-sidebar-right">
                        {/* Workflow Checklist */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Workflow</div>
                            <div className="qd-workflow-list">
                                {workflowItems.map(item => (
                                    <div key={item.id} className={`qd-workflow-item ${item.done ? 'done' : ''}`}>
                                        <div className="qd-workflow-check">
                                            {item.done ? (
                                                <i className="bi bi-check-circle-fill"></i>
                                            ) : (
                                                <i className="bi bi-circle"></i>
                                            )}
                                        </div>
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Quick Actions</div>
                            <div className="qd-quick-actions-grid">
                                <button className="qd-quick-action">
                                    <i className="bi bi-people"></i>
                                    <span>Manage Guests</span>
                                </button>
                                <button className="qd-quick-action">
                                    <i className="bi bi-folder"></i>
                                    <span>Upload Docs</span>
                                </button>
                                <button className="qd-quick-action">
                                    <i className="bi bi-receipt"></i>
                                    <span>Create Invoice</span>
                                </button>
                                <button className="qd-quick-action">
                                    <i className="bi bi-file-earmark"></i>
                                    <span>Create Voucher</span>
                                </button>
                            </div>
                        </div>

                        {/* Client Payment Summary */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Client Payment</div>
                            <div className="qd-summary-card">
                                <div className="qd-summary-row">
                                    <span>Invoice Amount</span>
                                    <span className="qd-summary-value">{formatCurrency(query.quoted_amount)}</span>
                                </div>
                                <div className="qd-summary-row qd-summary-success">
                                    <span>Received</span>
                                    <span className="qd-summary-value">{formatCurrency(0)}</span>
                                </div>
                                <div className="qd-summary-row qd-summary-danger">
                                    <span>Pending</span>
                                    <span className="qd-summary-value">{formatCurrency(query.quoted_amount)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Supplier Payment Summary */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">Supplier Payment</div>
                            <div className="qd-summary-card">
                                <div className="qd-summary-row">
                                    <span>Supplier Amount</span>
                                    <span className="qd-summary-value">{formatCurrency(0)}</span>
                                </div>
                                <div className="qd-summary-row qd-summary-success">
                                    <span>Paid</span>
                                    <span className="qd-summary-value">{formatCurrency(0)}</span>
                                </div>
                                <div className="qd-summary-row qd-summary-warning">
                                    <span>Pending</span>
                                    <span className="qd-summary-value">{formatCurrency(0)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Tasks */}
                        <div className="qd-info-section">
                            <div className="qd-section-title">
                                <span>Pending Tasks</span>
                                <button className="qd-add-btn">
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                            {tasks.length === 0 ? (
                                <div className="qd-no-data">No pending tasks</div>
                            ) : (
                                <div className="qd-task-mini-list">
                                    {/* Mini task list */}
                                </div>
                            )}
                        </div>

                        {/* Notes */}
                        {(query.requirements || query.internal_notes) && (
                            <div className="qd-info-section">
                                <div className="qd-section-title">Notes</div>
                                {query.requirements && (
                                    <div className="qd-note-block">
                                        <div className="qd-note-label">Requirements</div>
                                        <div className="qd-note-text">{query.requirements}</div>
                                    </div>
                                )}
                                {query.internal_notes && (
                                    <div className="qd-note-block qd-note-internal">
                                        <div className="qd-note-label">Internal Notes</div>
                                        <div className="qd-note-text">{query.internal_notes}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
